// Golf Persona System - Intelligently classify golfers and estimate their stats

export interface GolfPersona {
  id: string;
  name: string;
  description: string;
  icon: string;
  scoreRange: { min: number; max: number };
  estimatedHandicap: number;
  estimatedSwingSpeed: number;
  typicalBallFlight: string;
  skillLevel: string;
  equipmentNeeds: string[];
  adviceLevel: 'basic' | 'intermediate' | 'advanced';
  questionComplexity: 'simple' | 'moderate' | 'detailed';
}

export const GOLF_PERSONAS: GolfPersona[] = [
  {
    id: 'absolute-beginner',
    name: '🆕 Just Started',
    description: 'New to golf or played less than 5 rounds',
    icon: '🆕',
    scoreRange: { min: 120, max: 150 },
    estimatedHandicap: 40,
    estimatedSwingSpeed: 75,
    typicalBallFlight: 'slice',
    skillLevel: 'beginner',
    equipmentNeeds: ['Maximum forgiveness', 'Game improvement', 'Affordable'],
    adviceLevel: 'basic',
    questionComplexity: 'simple',
  },
  {
    id: 'weekend-warrior',
    name: '🏌️ Weekend Warrior',
    description: 'Play occasionally, having fun, want to break 100',
    icon: '🏌️',
    scoreRange: { min: 100, max: 120 },
    estimatedHandicap: 28,
    estimatedSwingSpeed: 85,
    typicalBallFlight: 'slice',
    skillLevel: 'beginner',
    equipmentNeeds: ['Forgiving clubs', 'Straight ball flight', 'Value'],
    adviceLevel: 'basic',
    questionComplexity: 'simple',
  },
  {
    id: 'committed-improver',
    name: '📈 Committed Improver',
    description: 'Regular player working toward breaking 90',
    icon: '📈',
    scoreRange: { min: 90, max: 100 },
    estimatedHandicap: 18,
    estimatedSwingSpeed: 90,
    typicalBallFlight: 'straight',
    skillLevel: 'intermediate',
    equipmentNeeds: ['Balance of distance and control', 'Consistency', 'Quality'],
    adviceLevel: 'intermediate',
    questionComplexity: 'moderate',
  },
  {
    id: 'bogey-golfer',
    name: '🎯 Bogey Golfer',
    description: 'Solid fundamentals, breaking 90 consistently',
    icon: '🎯',
    scoreRange: { min: 85, max: 95 },
    estimatedHandicap: 15,
    estimatedSwingSpeed: 95,
    typicalBallFlight: 'fade',
    skillLevel: 'intermediate',
    equipmentNeeds: ['Workability', 'Distance control', 'Feel'],
    adviceLevel: 'intermediate',
    questionComplexity: 'moderate',
  },
  {
    id: 'mid-handicapper',
    name: '🎖️ Mid Handicapper',
    description: 'Consistent player, aiming for low 80s',
    icon: '🎖️',
    scoreRange: { min: 82, max: 88 },
    estimatedHandicap: 12,
    estimatedSwingSpeed: 97,
    typicalBallFlight: 'fade',
    skillLevel: 'intermediate',
    equipmentNeeds: ['Distance and control', 'Shot shaping', 'Quality'],
    adviceLevel: 'intermediate',
    questionComplexity: 'moderate',
  },
  {
    id: 'low-handicapper',
    name: '⭐ Low Handicapper',
    description: 'Single digit handicap, fine-tuning skills',
    icon: '⭐',
    scoreRange: { min: 76, max: 82 },
    estimatedHandicap: 7,
    estimatedSwingSpeed: 100,
    typicalBallFlight: 'draw',
    skillLevel: 'advanced',
    equipmentNeeds: ['Precision', 'Shot shaping', 'Premium quality'],
    adviceLevel: 'advanced',
    questionComplexity: 'detailed',
  },
  {
    id: 'plus-handicapper',
    name: '🌟 Plus Handicapper',
    description: 'Elite player, shoots under par regularly',
    icon: '🌟',
    scoreRange: { min: 71, max: 76 },
    estimatedHandicap: 2,
    estimatedSwingSpeed: 105,
    typicalBallFlight: 'controlled-draw',
    skillLevel: 'advanced',
    equipmentNeeds: ['Tour-level precision', 'Shot shaping', 'Custom specs'],
    adviceLevel: 'advanced',
    questionComplexity: 'detailed',
  },
  {
    id: 'scratch-golfer',
    name: '🏆 Scratch/Pro',
    description: 'Tour-level skills, competing at highest level',
    icon: '🏆',
    scoreRange: { min: 65, max: 71 },
    estimatedHandicap: 0,
    estimatedSwingSpeed: 108,
    typicalBallFlight: 'controlled',
    skillLevel: 'advanced',
    equipmentNeeds: ['Tour-level performance', 'Custom everything', 'Exact specs'],
    adviceLevel: 'advanced',
    questionComplexity: 'detailed',
  },
];

// Get persona from average score
export function getPersonaFromScore(averageScore: number): GolfPersona {
  const persona = GOLF_PERSONAS.find(
    (p) => averageScore >= p.scoreRange.min && averageScore <= p.scoreRange.max
  );
  
  // If score is below scratch golfer range, return scratch golfer
  if (averageScore < 65) {
    return GOLF_PERSONAS[GOLF_PERSONAS.length - 1]; // Last persona (Scratch Golfer)
  }
  
  // If score is above absolute beginner range, return absolute beginner
  if (averageScore > 150) {
    return GOLF_PERSONAS[0]; // Absolute Beginner
  }
  
  return persona || GOLF_PERSONAS[2]; // Default to Committed Improver
}

// Estimate handicap from score - VERY GRANULAR with decimal precision
export function estimateHandicapFromScore(averageScore: number): number {
  // Use mathematical formula for smooth handicap estimation
  // Based on USGA formula: (Score - Course Rating) * 113 / Slope Rating
  // Simplified for typical course (Rating 72, Slope 113)
  
  const baseHandicap = (averageScore - 72) * 0.96;
  
  // Clamp between 0 and 54 (USGA max)
  const clampedHandicap = Math.max(0, Math.min(54, baseHandicap));
  
  // Round to 1 decimal place for precision
  return Math.round(clampedHandicap * 10) / 10;
}

// Estimate swing speed from score and age
export function estimateSwingSpeed(averageScore: number, age: number): number {
  const persona = getPersonaFromScore(averageScore);
  let baseSpeed = persona.estimatedSwingSpeed;
  
  // Age adjustment
  if (age < 30) baseSpeed += 5;
  else if (age > 50) baseSpeed -= 5;
  else if (age > 60) baseSpeed -= 10;
  else if (age > 70) baseSpeed -= 15;
  
  return Math.max(65, Math.min(120, baseSpeed));
}

// Get adaptive question set for persona
export function getQuestionsForPersona(persona: GolfPersona) {
  const baseQuestions = [
    'height',
    'weight',
    'age',
    'averageScore',
    'ballFlight',
  ];
  
  if (persona.questionComplexity === 'simple') {
    return baseQuestions;
  }
  
  if (persona.questionComplexity === 'moderate') {
    return [
      ...baseQuestions,
      'swingTempo',
      'missPattern',
      'preferredCourseType',
    ];
  }
  
  // detailed
  return [
    ...baseQuestions,
    'swingSpeed',
    'swingTempo',
    'missPattern',
    'preferredCourseType',
    'handSize',
    'gripType',
    'gripPressure',
    'attackAngle',
  ];
}

// Should show "I'm not sure" button for this question?
export function shouldShowAIEstimate(question: string, persona: GolfPersona): boolean {
  const technicalQuestions = ['swingSpeed', 'attackAngle', 'handSize', 'gripType'];
  
  // Beginners and intermediates get AI estimates for technical stuff
  if (technicalQuestions.includes(question)) {
    return persona.adviceLevel !== 'advanced';
  }
  
  return false;
}

// Generate AI-powered estimate explanation
export function getAIEstimateExplanation(question: string, persona: GolfPersona): string {
  const estimates: Record<string, string> = {
    swingSpeed: `Based on your ${persona.name} profile and typical ${persona.scoreRange.min}-${persona.scoreRange.max} scores, we estimate your swing speed around ${persona.estimatedSwingSpeed} mph.`,
    attackAngle: `Most ${persona.skillLevel} players have a ${persona.skillLevel === 'beginner' ? 'steep' : persona.skillLevel === 'intermediate' ? 'neutral' : 'slightly positive'} attack angle.`,
    handSize: `We'll recommend standard grip sizes which work for 80% of golfers. You can adjust later if needed.`,
    gripType: `We'll assume ${persona.skillLevel === 'beginner' ? 'baseball/10-finger' : 'overlap'} grip, the most common for your skill level.`,
  };
  
  return estimates[question] || `We'll estimate this based on your ${persona.name} profile.`;
}

// Get completion percentage
export function getProfileCompleteness(answeredQuestions: string[], totalQuestions: string[]): number {
  return Math.round((answeredQuestions.length / totalQuestions.length) * 100);
}

// Calculate value of additional data
export function calculateAdditionalDataValue(currentCompleteness: number): {
  percentImprovement: number;
  message: string;
} {
  if (currentCompleteness >= 80) {
    return {
      percentImprovement: 10,
      message: '10% more accurate recommendations with advanced details',
    };
  }
  
  if (currentCompleteness >= 60) {
    return {
      percentImprovement: 25,
      message: '25% better equipment matches with complete profile',
    };
  }
  
  return {
    percentImprovement: 40,
    message: '40% more personalized results with full data',
  };
}
